import { PlayerTranslationKeys } from '../../api/models/TranslationKeys';
import { GlobalSettingsKeys } from '../../api/models/GlobalSettingsKeys';
import { SelectSettingsPage } from '../../components/player/SelectSettingsPage';
import { Backend } from '../../api/Backend';
import { Lightning, Log, Storage } from '@lightningjs/sdk';
import { OptionData } from '../../components/option/OptionData';
import { AudioTrack, SubtitleTrack, VideoQuality } from './PlayerModel';

/**
 * @typedef {Object} MenuOption
 * @property {SettingNames} name
 * @property {*[]} subOptions
 */
export interface MenuOption {
  name: string;
  subOptions: any[];
}

export const SELECTED_LANGUAGE_KEY = 'selected_language';

/**
 * @enum {string}
 */
const SettingNames = {
  QUALITY: 'QUALITY',
  AUDIO: 'AUDIO',
  SUBTITLE: 'SUBTITLE',
};

export class PlayerSettingsManager {
  private _backend: Backend;
  private _settings: { hideFps: number; showTeletextId: boolean };
  private _hideAudioSelection = false;

  constructor(backend: Backend) {
    this._backend = backend;

    this._settings = {
      hideFps: backend.globalSettings.numberValue(GlobalSettingsKeys.playerQualityHideFps) || 25,
      showTeletextId: backend.globalSettings.boolValue(GlobalSettingsKeys.displaySubtitleTeletextId) || true,
    };
  }

  configStream(element: Lightning.Component) {
    const settingsOptions = this._createSettingsOptions(
      this._listQualityOptions(),
      this._listAudioOptions(),
      this._listSubtitleTracks()
    );
    if (settingsOptions.length > 1) {
      element.fireAncestors('$openDialog', {
        type: SelectSettingsPage,
        params: {
          name: 'Menu settings',
          title: this._backend.translations.forKey(PlayerTranslationKeys.player_settings_title),
          options: settingsOptions,
          onSuccess: (opt: OptionData) => {
            this._hideOptions(element);
            this._settingsSelected(element, opt.value, true);
          },
          onCancelled: () => this._hideOptions(element),
        },
      });
    } else if (settingsOptions.length === 1) {
      this._settingsSelected(element, settingsOptions[0]?.value);
    }
  }

  hasSettings() {
    return (
      this._createSettingsOptions(this._listQualityOptions(), this._listAudioOptions(), this._listSubtitleTracks())
        .length > 0
    );
  }

  _createSettingsOptions(
    videoOptions: OptionData[],
    audioOptions: OptionData[],
    subtitleOptions: OptionData[]
  ): OptionData[] {
    const settingsOptions: OptionData[] = [];
    if (videoOptions.length > 1) {
      const translation = this._backend.translations.forKey(PlayerTranslationKeys.player_settings_quality);
      const selectedQuality = this._backend.playerController.playerService.selectedVideoQuality;
      settingsOptions.push({
        text: translation.replace('$1', this._convertQualityOptionText(selectedQuality)),
        value: {
          name: SettingNames.QUALITY,
          subOptions: videoOptions,
        },
        options: [],
      });
    }
    // check if audio selection is allowed by player policy
    if (!this._hideAudioSelection && audioOptions.length > 1) {
      const translation = this._backend.translations.forKey(PlayerTranslationKeys.player_settings_audio);
      const selectedTrack = this._backend.playerController.playerService.selectedAudioTrack;
      if (selectedTrack) {
        settingsOptions.push({
          text: translation.replace('$1', this._convertAudioOptionText(selectedTrack, false)),
          value: {
            name: SettingNames.AUDIO,
            subOptions: audioOptions,
          },
          options: [],
        });
      }
    }
    if (subtitleOptions.length > 1) {
      const translation = this._backend.translations.forKey(PlayerTranslationKeys.player_settings_subtitle);
      const selectedSubtitle = this._backend.playerController.playerService.selectedSubtitleTrack;
      settingsOptions.push({
        text: translation.replace('$1', this._convertSubtitleOptionText(selectedSubtitle)),
        value: {
          name: SettingNames.SUBTITLE,
          subOptions: subtitleOptions,
        },
        options: [],
      });
    }
    return settingsOptions;
  }

  /**
   * @param element
   * @param {MenuOption} val
   * @param {boolean} [userSelected]
   * @private
   */
  _settingsSelected(element: Lightning.Component, val: MenuOption, userSelected = false) {
    let title = '';
    switch (val.name) {
      case SettingNames.QUALITY:
        title = this._backend.translations.forKey(PlayerTranslationKeys.player_settings_quality_title);
        this._showSubDialog(element, title, val.subOptions, userSelected, this._qualitySelected.bind(this));
        break;
      case SettingNames.AUDIO:
        title = this._backend.translations.forKey(PlayerTranslationKeys.player_settings_audio_title);
        this._showSubDialog(element, title, val.subOptions, userSelected, this._audioTrackSelected.bind(this));
        break;
      case SettingNames.SUBTITLE:
        title = this._backend.translations.forKey(PlayerTranslationKeys.player_settings_subtitles_title);
        this._showSubDialog(element, title, val.subOptions, userSelected, this._subtitleTrackSelected.bind(this));
        break;
    }
  }

  _showSubDialog(
    element: Lightning.Component,
    title: string,
    options: OptionData[],
    userSelected: boolean,
    handler: (val: any) => void
  ) {
    element.fireAncestors('$openDialog', {
      type: SelectSettingsPage,
      params: {
        name: title,
        title: title,
        options: options,
        onSuccess: (opt: OptionData) => {
          this._hideOptions(element);
          handler(opt.value);
        },
        onCancelled: () => this._handleSubDialogClose(element, userSelected),
      },
    });
  }

  _handleSubDialogClose(element: Lightning.Component, userSelected: boolean) {
    if (userSelected) {
      this.configStream(element);
    } else {
      this._hideOptions(element);
    }
  }

  _hideOptions(element: Lightning.Component) {
    element.fireAncestors('$closeDialog');
  }

  _qualitySelected(quality: VideoQuality) {
    this._backend.playerController.playerService.setVideoQuality(quality);
  }

  _audioTrackSelected(track: AudioTrack) {
    // save audio track
    Storage.set(SELECTED_LANGUAGE_KEY, track.language);
    this._backend.playerController.playerService.setAudioTrack(track);
  }

  _subtitleTrackSelected(track: SubtitleTrack) {
    this._backend.playerController.playerService.setSubtitleTrack(track);
  }

  _listQualityOptions(): OptionData[] {
    const qualities = this._backend.playerController.playerService.videoQualities;
    qualities.sort((a, b) => {
      const cmp = b.height - a.height;
      if (cmp === 0 && b.framerate && a.framerate) {
        return b.framerate - a.framerate;
      }
      return cmp;
    });
    const options: OptionData[] = [];
    options.push({
      text: this._convertQualityOptionText(undefined),
      value: undefined,
      selected: qualities.findIndex(q => q.active) === -1,
      options: [],
    });
    qualities.forEach(q => {
      options.push({
        text: this._convertQualityOptionText(q),
        value: q,
        selected: q.active,
        options: [],
      });
    });
    return options;
  }

  /**
   * @param {VideoQuality} quality
   * @returns {string}
   * @private
   */
  _convertQualityOptionText(quality: VideoQuality | undefined): string {
    if (!quality) {
      return this._backend.translations.forKey(PlayerTranslationKeys.player_quality_auto);
    }
    if (!quality.framerate || quality.framerate === this._settings.hideFps) {
      const qualityText = this._backend.translations.forKey(PlayerTranslationKeys.player_quality_unit);
      return qualityText.replace('$1', quality.height.toString());
    } else {
      const qualityText = this._backend.translations.forKey(PlayerTranslationKeys.player_quality_unit_fps);
      return qualityText.replace('$1', quality.height.toString()).replace('$2', quality.framerate.toString());
    }
  }

  _listAudioOptions(): OptionData[] {
    const tracks = this._backend.playerController.playerService.audioTracks;
    return tracks.map(t => ({
      text: this._convertAudioOptionText(t, this._checkAddAudioChannelsText(t, tracks)),
      value: t,
      selected: t.active,
      options: [],
    }));
  }

  /**
   * @param {AudioTrack} track
   * @param {AudioTrack[]} tracks
   * @returns {boolean}
   * @private
   */
  _checkAddAudioChannelsText(track: AudioTrack, tracks: AudioTrack[]): boolean {
    return tracks.filter(el => el.language === track.language || el.label === track.label).length > 1;
  }

  /**
   * @param {AudioTrack} track
   * @param {boolean} addChannelsText
   * @private
   */
  _convertAudioOptionText(track: AudioTrack, addChannelsText: boolean): string {
    let result = track.language || track.label;
    if (result) {
      result = this.languageCodeTranslation(result);
    }
    if (addChannelsText) {
      if (track.channelsCount === 2) {
        result += ` (${this._backend.translations.forKey(PlayerTranslationKeys.player_audio_channels_2)})`;
      } else if (track.channelsCount === 6) {
        result += ` (${this._backend.translations.forKey(PlayerTranslationKeys.player_audio_channels_6)})`;
      }
    }
    return result || '';
  }

  _listSubtitleTracks(): OptionData[] {
    const tracks = this._backend.playerController.playerService.subtitleTracks;
    const options: OptionData[] = [];
    options.push({
      text: this._convertSubtitleOptionText(undefined),
      value: undefined,
      selected: tracks.findIndex(q => q.active) === -1,
      options: [],
    });
    tracks.forEach(t => {
      options.push({
        text: this._convertSubtitleOptionText(t),
        value: t,
        selected: t.active,
        options: [],
      });
    });
    return options;
  }

  _convertSubtitleOptionText(track: SubtitleTrack | undefined): string {
    if (!track) {
      return this._backend.translations.forKey(PlayerTranslationKeys.player_subtitle_none);
    } else {
      let option = track.language || track.label;
      if (option) {
        option = this.languageCodeTranslation(option);
      }
      if (this._settings.showTeletextId && track.representationId) {
        const split = track.representationId.includes('=') ? track.representationId.split('=') : undefined;
        if (split && split.length > 0 && split[0] !== '=') {
          option += (option ? ' ' : '') + `(${split[0]})`;
        }
      }
      return option || '';
    }
  }

  private languageCodeTranslation(code: string) {
    const trnKey = PlayerTranslationKeys.player_language_xx.replace('xx', code);
    const trnLang = this._backend.translations.forKey(trnKey);
    if (trnLang.startsWith('*')) {
      Log.warn('Player language code translation key not found', code);
      return code;
    } else {
      return trnLang;
    }
  }

  set hideAudioSelection(val: boolean) {
    this._hideAudioSelection = val;
  }
}
